import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Download } from "lucide-react";
import { fmtDate } from "@/lib/api";
import { AdminTitle, Spinner } from "@/admin/AdminLayout";
import { DOC_KINDS, Empty, Listing, download, useList } from "@/admin/hr/common";
import { DocViewer, docUrl } from "@/admin/hr/DocViewer";

function MissingList({ apps }) {
  return (
    <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200 bg-white" data-testid="docs-missing-list">
      {apps.map((a) => (
        <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
          <span><b className="text-[color:var(--red)]">{a.app_no}</b> · {a.name}</span>
          <span className="text-xs text-orange-700">Eksik: {(a.missing_docs || []).join(", ")}</span>
          <Link to={`/admin/basvurular?id=${a.id}`} className="text-xs font-semibold underline">Başvuruyu aç</Link>
        </li>
      ))}
    </ul>
  );
}

function FileTable({ files, setView }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white">
      <table className="w-full text-sm" data-testid="docs-table">
        <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500"><tr>{["Başvuru No", "Ad Soyad", "Belge", "Dosya", "Yükleme", ""].map((h) => <th key={h} className="px-3 py-3">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-zinc-100">
          {files.map((d) => (
            <tr key={d.id} data-testid={`doc-file-${d.id}`}>
              <td className="whitespace-nowrap px-3 py-3 font-semibold text-[color:var(--red)]">{d.app_no}</td>
              <td className="px-3 py-3">{d.name}</td>
              <td className="px-3 py-3">{d.kind_label}</td>
              <td className="px-3 py-3 text-xs text-zinc-500">{d.filename} · {(d.size / 1024).toFixed(0)} KB</td>
              <td className="whitespace-nowrap px-3 py-3 text-zinc-500">{fmtDate(d.uploaded_at)}</td>
              <td className="whitespace-nowrap px-3 py-3">
                <button onClick={() => setView(d)} className="rounded p-1.5 hover:bg-zinc-100" aria-label="Görüntüle" data-testid={`docs-view-${d.id}`}><Eye className="h-4 w-4" /></button>
                <button onClick={() => download(docUrl(d.app_id, d), d.filename, { params: { download: 1 } })} className="rounded p-1.5 hover:bg-zinc-100" aria-label="İndir" data-testid={`docs-download-${d.id}`}><Download className="h-4 w-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Results({ data, missing, setView }) {
  if (!data) return <Spinner />;
  if (missing) return <Listing rows={data.apps} empty={<Empty text="Belgesi eksik başvuru yok." testid="docs-missing-empty" />}><MissingList apps={data.apps} /></Listing>;
  return <Listing rows={data.files} empty={<Empty text="Belge bulunamadı." testid="docs-empty" />}><FileTable files={data.files} setView={setView} /></Listing>;
}

export default function Documents() {
  const [f, setF] = useState({ q: "", kind: "", missing: "" });
  const [data] = useList("/admin/hr/documents", f);
  const [view, setView] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div>
      <AdminTitle title="Başvuru Belgeleri" desc="Fotoğraf, kimlik kartı ve özgeçmiş dosyaları yalnızca yetkili yönetici tarafından görüntülenebilir." />
      <div className="mb-4 flex flex-wrap gap-2" role="tablist">
        {[["", "Tüm belgeler"], ["1", "Belgesi eksik başvurular"]].map(([v, l]) => (
          <button key={l} onClick={() => setF({ ...f, missing: v })} className={`rounded-md px-4 py-2 text-sm font-semibold ${f.missing === v ? "bg-[color:var(--ink)] text-white" : "bg-white text-zinc-700 border border-zinc-200"}`} data-testid={`docs-tab-${v || "all"}`}>{l}</button>
        ))}
      </div>
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <input value={f.q} onChange={set("q")} placeholder="Ad soyad, telefon veya başvuru no..." className="field-input" data-testid="docs-filter-q" />
        {!f.missing && <select value={f.kind} onChange={set("kind")} className="field-input" data-testid="docs-filter-kind"><option value="">Tüm belge türleri</option>{Object.entries(DOC_KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>}
      </div>
      <Results data={data} missing={f.missing} setView={setView} />
      <DocViewer appId={view?.app_id} doc={view} onClose={() => setView(null)} />
    </div>
  );
}
